import Button from "@components/ui/buttons/Button";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useMemo, useState } from "react";
import DataGrid from "@components/ui/tables/DataGrid";
import { transformBroadcastsToTable } from "@components/ui/tables/transformToTables";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { CreateBroadcastModal } from "@components/modals/CreateBroadcastModal";
import { ModifyBroadcastModal } from "@components/modals/ModifyBroadcastModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { Broadcast } from "types";
import { showNotification } from "context/notification.service";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";
import { isOwnerOrAdmin } from "@utils/permissions";

const statusTabs = [
  { value: "all", label: "Toutes" },
  { value: "active", label: "Actives" },
  { value: "inactive", label: "Inactives" },
];

export default function BroadcastsScreen() {
  const allBroadcasts = useProjectStore((state) => state.broadcasts);
  const removeBroadcasts = useProjectStore((state) => state.removeBroadcasts);
  const userData = useDataStore((state) => state.userData);
  // Memoize: the non-admin branch builds a fresh array each render, which
  // is referenced by a downstream useEffect — without useMemo every render
  // re-fires that effect's setState and we hit "Maximum update depth
  // exceeded", which jams react-router so sidebar nav can't take effect.
  const broadcasts = useMemo(
    () =>
      isOwnerOrAdmin(userData)
        ? allBroadcasts
        : allBroadcasts.filter((b) => b.author?.id === userData?.id),
    [allBroadcasts, userData],
  );
  const [table, setTable] = useState<any>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [modifyBroadcast, setModifyBroadcast] = useState<Broadcast | null>(
    null,
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => {
    const table = transformBroadcastsToTable(broadcasts, activeTab);
    setTable(table);
  }, [broadcasts, activeTab]);

  const handleRowClick = (row: any) => {
    const broadcast = broadcasts.find((b) => b.id === row.id);
    if (broadcast) {
      setModifyBroadcast(broadcast);
    }
  };

  const handleDeleteSelected = () => {
    removeBroadcasts(selectedIds);
    showNotification({
      type: "success",
      message: `${selectedIds.length} annonce(s) supprimée(s)`,
    });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {createModalOpen && (
        <CreateBroadcastModal onClose={() => setCreateModalOpen(false)} />
      )}
      {modifyBroadcast && (
        <ModifyBroadcastModal
          onClose={() => setModifyBroadcast(null)}
          initialBroadcast={modifyBroadcast}
        />
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} annonce(s) ?`}
          message="Cette action est irréversible. Toutes les annonces sélectionnées seront supprimées définitivement."
          confirmText="CONFIRMER"
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-400">
            <Text1>Annonces</Text1>
            <Text2>Retrouvez toutes vos annonces ici.</Text2>
          </div>
          <div className="flex flex-row gap-2 items-start">
            {selectedIds.length > 0 && (
              <SecondaryButton
                danger={true}
                startIcon={
                  <Icon1 name="delete" size={22} color={colors.red_500} />
                }
                label={`Supprimer la sélection (${selectedIds.length})`}
                variant="large"
                onClick={() => setShowDeleteConfirm(true)}
              />
            )}
            <Button
              label="Nouvelle annonce"
              onClick={() => setCreateModalOpen(true)}
            />
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col w-full">
        {table && (
          <div className="flex-1 min-h-0 max-w-1p2k w-full">
            <DataGrid
              selectable={true}
              table={table}
              hide_lines={true}
              tabs={statusTabs}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab)}
              onClick={handleRowClick}
              handleSelectionModelChange={(ids) => setSelectedIds(ids)}
              storageKey="broadcasts"
            />
          </div>
        )}
      </div>
    </div>
  );
}
